"use client"

import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getUserSession, revokeUserSession, terminateAllOtherSessions, terminateSession } from "../redux/slices/authSlice"
import { UserSessionsSkeleton } from "../components/skeletons"
import TerminateConfirmationModal from "../components/TerminateConfirmationModal"

const UserSessions = () => {
  const dispatch = useDispatch()
  const sessions = useSelector((state) => state.auth.sessions || [])
  const isLoading = useSelector((state) => state.auth.isLoading)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState("")
  const [isTerminatingAll, setIsTerminatingAll] = useState(false)
  const [terminateAllError, setTerminateAllError] = useState("")
  const [terminateAllSuccess, setTerminateAllSuccess] = useState("")
  const [showTerminateModal, setShowTerminateModal] = useState(false)
  const [showTerminateAllModal, setShowTerminateAllModal] = useState(false)
  const [sessionToTerminate, setSessionToTerminate] = useState(null)
  const [isTerminating, setIsTerminating] = useState(false)

  useEffect(() => {
    document.title = "Active Sessions - KAXAA Wallet"
    
    const fetchSessions = async () => {
      await dispatch(getUserSession())
    }

    fetchSessions()
  }, [dispatch])

  const handleTerminateSession = (sessionId) => {
    setSessionToTerminate(sessionId)
    setShowTerminateModal(true)
  }

  const confirmTerminateSession = async () => {
    if (!sessionToTerminate) return
    
    setIsTerminating(true)
    try {
      const result = await dispatch(terminateSession(sessionToTerminate))
      
      if (terminateSession.fulfilled.match(result)) {
        // Refresh sessions after termination
        await dispatch(getUserSession())
        setShowTerminateModal(false)
        setSessionToTerminate(null)
      } else {
        console.error("Failed to terminate session:", result)
      }
    } catch (error) {
      console.error("Terminate session error:", error)
    } finally {
      setIsTerminating(false)
    }
  }

  const handleTerminateAllOthers = () => {
    setShowTerminateAllModal(true)
  }

  const confirmTerminateAllOthers = async () => {
    setIsTerminatingAll(true);
    setTerminateAllError("");
    setTerminateAllSuccess("");

    try {
      const result = await dispatch(terminateAllOtherSessions());

      if (terminateAllOtherSessions.fulfilled.match(result)) {
        // Success - show success message
        setTerminateAllSuccess("All other sessions have been terminated successfully!");
        setShowTerminateAllModal(false);

        // Refresh the session list after a short delay
        setTimeout(async () => {
          await dispatch(getUserSession());
          setTerminateAllSuccess(""); // Clear success message after refresh
        }, 2000);
      } else {
        // Handle API error
        setTerminateAllError("Failed to terminate other sessions. Please try again.");
      }
    } catch (error) {
      console.error("Terminate all sessions error:", error);
      setTerminateAllError("An error occurred while terminating sessions.");
    } finally {
      setIsTerminatingAll(false);
    }
  }

  const handleRefreshSessions = async () => {
    setIsRefreshing(true);
    setRefreshError("");

    try {
      const result = await dispatch(getUserSession());
      console.log("API Response:", result); // Debug log

      if (getUserSession.fulfilled.match(result)) {
        // Success - sessions are automatically updated in the store
        console.log("Sessions refreshed successfully", result.payload);
        // Refresh the page after successful API call
        setIsRefreshing(false);
        window.location.reload();
      } else {
        // Handle API error
        console.log("API Error:", result);
        setRefreshError("Failed to refresh sessions. Please try again.");
        setIsRefreshing(false);
      }
    } catch (error) {
      console.error("Refresh sessions error:", error);
      setRefreshError("An error occurred while refreshing sessions.");
      setIsRefreshing(false);
    }
    // Note: No finally block needed since page reloads on success
  }

  const renderUserAgent = (userAgent) => {
    // Example userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/123.0.0.0"
    let browser = "Unknown Browser"
    let os = "Unknown OS"

    if (/Chrome/i.test(userAgent)) browser = "Chrome"
    else if (/Firefox/i.test(userAgent)) browser = "Firefox"
    else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = "Safari"
    else if (/Edge/i.test(userAgent)) browser = "Edge"

    if (/Windows/i.test(userAgent)) os = "Windows"
    else if (/Mac OS|Macintosh|MacOS/i.test(userAgent)) os = "MacOS"
    else if (/Linux/i.test(userAgent)) os = "Linux"
    else if (/Android/i.test(userAgent)) os = "Android"
    else if (/iPhone|iPad|iOS/i.test(userAgent)) os = "iOS"

    return `${browser} on ${os}`
  }

  const getDeviceIcon = (device) => {
    if (device.includes("iPhone") || device.includes("Android")) {
      return "📱"
    } else if (device.includes("MacOS")) {
      return "💻"
    } else if (device.includes("Windows")) {
      return "🖥️"
    } else if (device.includes("Linux")) {
      return "🐧"
    }
    return "🌐"
  }

  return (
    <div className="wallet-dashboard user-sessions">
      <>
        <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
        <div className="">
          {/* Header Section */}
          <div className="mb-6">
            <h1 className="font-semibold text-18 m-0">Kaxaa Token - Active Sessions</h1>
            <p className="text-muted-foreground">Manage your logged-in sessions across all devices</p>
          </div>

          {/* Stats Cards */}
          {/*
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="card border">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm mb-2 font-medium text-muted-foreground">Total Sessions</p>
                    <p className="text-2xl m-0 font-bold text-foreground">{isLoading ? "Loading..." : sessions?.length}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm mb-2 font-medium text-muted-foreground">Active Now</p>
                    <p className="text-2xl m-0 font-bold text-green-600">{sessions.filter((s) => s?.isCurrent).length}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm mb-2 font-medium text-muted-foreground">Other Devices</p>
                    <p className="text-2xl m-0 font-bold text-orange-600">{sessions.filter((s) => !s?.isCurrent).length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          */}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 mb-6">

            <button
              className={`auth-button w-auto sm:flex-none d-flex align-items-center justify-content-center gap-2 ${isTerminatingAll ? 'disabled' : ''}`}
              onClick={handleTerminateAllOthers}
              disabled={isTerminatingAll}
              style={{
                backgroundColor: 'var(--danger-color)',
                color: '#ffffff'
              }}
            >
              {isTerminatingAll ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                    style={{
                      borderColor: '#ffffff',
                      borderRightColor: 'transparent'
                    }}
                  ></span>
                  <span>Terminating...</span>
                </>
              ) : (
                <span>Terminate All Other Sessions</span>
              )}
            </button>
            <button
              className={`btn btn-outline-primary d-flex align-items-center justify-content-center gap-2 fw-medium w-auto sm:flex-none ${isRefreshing ? 'disabled' : ''}`}
              onClick={handleRefreshSessions}
              disabled={isRefreshing}
              style={{
                borderColor: 'var(--accent-color)',
                color: '#ffffff',
                backgroundColor: 'var(--accent-color)'
              }}
            >
              {isRefreshing ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                    style={{
                      borderColor: '#ffffff',
                      borderRightColor: 'var(--accent-color)',
                    }}
                  ></span>
                  <span>Refreshing...</span>
                </>
              ) : (
                <span>Refresh Sessions</span>
              )}
            </button>
          </div>

          {/* Success Message */}
          {terminateAllSuccess && (
            <div className="alert alert-success mb-6 d-flex gap-2 align-items-center" role="alert">
              <svg
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                className="me-2"
              >
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.061L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
              </svg>
              {terminateAllSuccess}
            </div>
          )}

          {/* Error Messages */}
          {refreshError && (
            <div className="error-alert mb-6">
              {refreshError}
            </div>
          )}

          {terminateAllError && (
            <div className="error-alert mb-6">
              {terminateAllError}
            </div>
          )}

          {/* Sessions List */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground mb-2">Your Sessions</h2>

            {isLoading ? (
              <UserSessionsSkeleton />
            ) : sessions && sessions.length > 0 ? sessions.map((session) => (
              <div
                key={session.id}
                className={`card transition-all duration-200 hover:shadow-md border ${session.isCurrent ? "border-primary" : ""} rounded-lg`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getDeviceIcon(session.device)}</span>
                      <div>
                        <div className="flex items-center gap-2 font-semibold">
                          {renderUserAgent(session.device)}
                          {session.isCurrent && (
                            <label className="">
                              Current Session
                            </label>
                          )}
                        </div>
                        <div className="mt-1">
                          {session.location.country} {session.location.city} • IP: {session.ipAddress}
                        </div>
                      </div>
                    </div>

                    {session.isCurrent && (
                      <p className="text-muted">This is your current session</p>
                    )}
                    {!session.isCurrent && (
                      <button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTerminateSession(session.id)}
                        className="text-destructive hover:text-destructive-foreground hover:bg-destructive link-button"
                      >
                        Terminate
                      </button>
                    )}
                  </div>
                </div>

                <div className="pt-0 px-3 pb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-muted-foreground">Last Active:</span>
                      <span className="ml-2 text-foreground">{new Date(session.lastActiveAt).toLocaleString()}</span>
                    </div>
                    <div className="text-end">
                      <span className="font-medium text-muted-foreground">Login Time:</span>
                      <span className="ml-2 text-foreground">{new Date(session.createdAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  {isLoading ? "Loading sessions..." : "No active sessions found."}
                </p>
                {!isLoading && (
                  <button
                    onClick={handleRefreshSessions}
                    className="link-button mt-2"
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? "Refreshing..." : "Refresh Sessions"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Security Notice */}
          {/* <div className="mt-8 bg-muted/50">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <span className="text-xl">🛡️</span>
            <div>
              <h3 className="font-semibold text-foreground mb-2">Security Notice</h3>
              <p className="text-sm text-muted-foreground">
                If you notice any suspicious sessions or unrecognized devices, terminate them immediately and consider
                changing your password. Always log out from public or shared devices.
              </p>
            </div>
          </div>
        </div>
      </div> */}

          {/* Terminate Single Session Confirmation Modal */}
          <TerminateConfirmationModal
            isOpen={showTerminateModal}
            title="Terminate Session"
            message="Are you sure you want to terminate this session?"
            description="This will log out the device from your account. You'll need to log in again on that device."
            primaryButtonText="Terminate"
            secondaryButtonText="Cancel"
            onPrimaryClick={confirmTerminateSession}
            onSecondaryClick={() => {
              setShowTerminateModal(false)
              setSessionToTerminate(null)
            }}
            isLoading={isTerminating}
          />

          {/* Terminate All Other Sessions Confirmation Modal */}
          <TerminateConfirmationModal
            isOpen={showTerminateAllModal}
            title="Terminate All Other Sessions"
            message="Are you sure you want to terminate all other sessions?"
            description={`This will log out ${sessions.filter(s => !s.isCurrent).length} other device(s) from your account. You'll remain logged in on this device.`}
            primaryButtonText="Terminate All"
            secondaryButtonText="Cancel"
            onPrimaryClick={confirmTerminateAllOthers}
            onSecondaryClick={() => setShowTerminateAllModal(false)}
            isLoading={isTerminatingAll}
          />
        </div>
      </>
    </div>
  )
}

export default UserSessions
